import { useEffect } from "react";
import type { EngagementProvider } from "@/provider/EngagementProvider";
import type { ProviderRuntimeSnapshot } from "@/provider/ProviderTypes";
import type {
	ShellRuntimeAction,
	ShellRuntimeState,
} from "@/shell/ShellRuntimeReducer";
import { debugLog } from "@/utils";
import { resolvePopupControllerAction } from "@/shell/ShellPopupPolicy";

export type UseShellPopupControllerInput = {
	hostTransportReady: boolean;
	hostConfigured: boolean;
	hostGameReady: boolean;
	providerBootEligible: boolean;

	provider: EngagementProvider | null;
	providerReady: boolean;
	providerRuntimeSnapshot: ProviderRuntimeSnapshot;

	activeBootGeneration: number;
	readyBootGeneration: number | null;
	inFlightBootGeneration: number | null;

	state: ShellRuntimeState;
	dispatch: React.Dispatch<ShellRuntimeAction>;

	triggerFreshProviderBoot: () => void;
};

export function useShellPopupController({
	hostTransportReady,
	hostConfigured,
	hostGameReady,
	providerBootEligible,
	provider,
	providerReady,
	providerRuntimeSnapshot,
	activeBootGeneration,
	readyBootGeneration,
	inFlightBootGeneration,
	state,
	dispatch,
	triggerFreshProviderBoot,
}: UseShellPopupControllerInput): void {
	useEffect(() => {
		const resolution = resolvePopupControllerAction({
			state,
			provider,
			providerReady,
			providerRuntimeSnapshot,
			activeBootGeneration,
			readyBootGeneration,
		});

		if (resolution.kind === "noop") {
			return;
		}

		switch (resolution.reason) {
			case "desired_close": {
				debugLog(
					"[EF ShellPopupController] desired popup open resolved to close state",
					{
						currentPopupPhase: state.popupPhase,
						nextPhase:
							resolution.action.type === "CLEAR_PENDING_POPUP_AND_CLOSE"
								? resolution.action.payload.nextPhase
								: null,
						pendingPopupGeneration: state.pendingPopupGeneration,
						popupGranted: state.popupState?.granted ?? false,
						modalHasContent: state.modalHasContent,
					}
				);

				dispatch(resolution.action);
				return;
			}

			case "desired_request_fresh_boot": {
				debugLog(
					"[EF ShellPopupController] popup open requires fresh provider boot",
					{
						hostTransportReady,
						hostConfigured,
						hostGameReady,
						providerBootEligible,
						providerRuntimePhase: providerRuntimeSnapshot.phase,
						activeBootGeneration,
						readyBootGeneration,
						inFlightBootGeneration,
						nextExpectedGeneration:
							resolution.action.type ===
							"PREPARE_FRESH_BOOT_FOR_POPUP_OPEN"
								? resolution.action.payload.nextExpectedGeneration
								: null,
						modalHasContent: state.modalHasContent,
					}
				);

				dispatch(resolution.action);
				triggerFreshProviderBoot();
				return;
			}

			case "desired_open_now": {
				debugLog(
					"[EF ShellPopupController] desired popup open can proceed immediately",
					{
						nextPhase:
							resolution.action.type === "OPEN_POPUP_NOW"
								? resolution.action.payload.nextPhase
								: null,
						activeBootGeneration,
						readyBootGeneration,
						popupGranted: state.popupState?.granted ?? false,
						modalHasContent: state.modalHasContent,
					}
				);

				dispatch(resolution.action);
				return;
			}

			case "desired_wait_provider": {
				debugLog(
					"[EF ShellPopupController] desired popup open waiting for current provider generation",
					{
						providerReady,
						hasProvider: !!provider,
						hostTransportReady,
						hostConfigured,
						hostGameReady,
						providerBootEligible,
						providerRuntimePhase: providerRuntimeSnapshot.phase,
						activeBootGeneration,
						readyBootGeneration,
						inFlightBootGeneration,
						nextPendingPopupGeneration:
							resolution.action.type === "WAIT_FOR_PROVIDER"
								? resolution.action.payload.generation
								: null,
						modalHasContent: state.modalHasContent,
					}
				);

				dispatch(resolution.action);
				return;
			}

			case "pending_generation_ready": {
				debugLog(
					"[EF ShellPopupController] pending popup generation is now ready",
					{
						popupPhase: state.popupPhase,
						pendingPopupGeneration: state.pendingPopupGeneration,
						providerRuntimePhase: providerRuntimeSnapshot.phase,
						activeBootGeneration,
						readyBootGeneration,
						popupGranted: state.popupState?.granted ?? false,
						nextPhase:
							resolution.action.type === "PROMOTE_PENDING_POPUP_TO_OPEN"
								? resolution.action.payload.nextPhase
								: null,
						modalHasContent: state.modalHasContent,
					}
				);

				dispatch(resolution.action);
				return;
			}

			case "opening_granted": {
				debugLog("[EF ShellPopupController] popup phase opening -> open", {
					popupPhase: state.popupPhase,
					popupGranted: state.popupState?.granted ?? false,
					modalHasContent: state.modalHasContent,
				});

				dispatch(resolution.action);
				return;
			}

			case "closing_ack": {
				debugLog("[EF ShellPopupController] popup phase closing -> closed", {
					popupPhase: state.popupPhase,
					popupGranted: state.popupState?.granted ?? false,
					modalHasContent: state.modalHasContent,
				});

				dispatch(resolution.action);
				return;
			}
		}
	}, [
		state,
		hostTransportReady,
		hostConfigured,
		hostGameReady,
		providerBootEligible,
		provider,
		providerReady,
		providerRuntimeSnapshot,
		activeBootGeneration,
		readyBootGeneration,
		inFlightBootGeneration,
		dispatch,
		triggerFreshProviderBoot,
	]);
}

export default useShellPopupController;